/**
 * Bitcoin & Bitcoin Cash API Service
 *
 * Bitcoin: Blockchain.com API (no rate limit documented)
 * Bitcoin Cash: Blockchain.com API (primary) -> Blockchair API (fallback, 1,440 requests/day)
 *
 * Documentation:
 * - https://www.blockchain.com/api/blockchain_api
 * - https://blockchair.com/api/docs
 */

import { getBitcoinAddressData as getBlockchairData } from './blockchairService';

const BLOCKCHAIN_API_BASE = 'https://blockchain.info';

/**
 * Fetch Bitcoin/Bitcoin Cash address data
 * @param {string} address - Bitcoin or Bitcoin Cash address
 * @param {string} chain - Chain identifier ('bitcoin' or 'bitcoincash')
 * @returns {Promise<object>} Address data including balance, transactions, etc.
 */
export const getBitcoinAddressData = async (address, chain = 'bitcoin') => {
  try {
    // Bitcoin Cash: Try Blockchain.com first, fall back to Blockchair if CORS fails
    if (chain === 'bitcoincash') {
      // Remove 'bitcoincash:' prefix if present for Blockchain.com API
      const cleanAddress = address.replace('bitcoincash:', '');

      try {
        const url = `${BLOCKCHAIN_API_BASE}/bch/rawaddr/${cleanAddress}?limit=50`;

        const response = await fetch(url, {
          mode: 'cors',
          credentials: 'omit'
        });

        if (!response.ok) {
          throw new Error(`Blockchain.com BCH returned ${response.status}`);
        }

        const data = await response.json();

        // Validate response
        if (!data.address || data.final_balance === undefined) {
          throw new Error('Invalid response from Blockchain.com BCH API');
        }

        return {
          address: data.address,
          balance: data.final_balance,
          totalReceived: data.total_received,
          totalSent: data.total_sent,
          transactionCount: data.n_tx,
          transactions: data.txs || []
        };
      } catch (bchError) {
        console.warn('Blockchain.com BCH unavailable, trying Blockchair:', bchError.message);
        return await getBlockchairData(cleanAddress, 'bitcoincash');
      }
    }

    // Bitcoin uses Blockchain.com
    const url = `${BLOCKCHAIN_API_BASE}/rawaddr/${address}?limit=50`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorMsg = `Blockchain.com API returned ${response.status}`;
      console.warn(errorMsg);
      throw new Error(errorMsg);
    }

    const data = await response.json();

    // Validate response structure
    if (!data.address || data.final_balance === undefined) {
      throw new Error('Invalid response from Blockchain.com API');
    }

    return {
      address: data.address,
      balance: data.final_balance,
      totalReceived: data.total_received,
      totalSent: data.total_sent,
      transactionCount: data.n_tx,
      transactions: data.txs || []
    };
  } catch (error) {
    console.warn('Blockchain.com API unavailable:', error.message);
    throw error;
  }
};

/**
 * Format Bitcoin balance from satoshis to BTC
 * @param {number} satoshis - Balance in satoshis
 * @returns {string} Balance in BTC
 */
export const formatBitcoinBalance = (satoshis) => {
  return (satoshis / 100000000).toFixed(8);
};

/**
 * Calculate transaction totals from transaction history
 * Works with both Blockchain.com and Blockchair formats
 * @param {array} transactions - Transaction history
 * @param {string} address - Bitcoin/BCH address to calculate for
 * @param {number} totalReceived - Total received in satoshis (from API)
 * @param {number} totalSent - Total sent in satoshis (from API)
 * @param {string} chain - Chain identifier for format detection
 * @returns {object} Transaction totals
 */
export const calculateBitcoinTransactionTotals = (transactions, address, totalReceived, totalSent, chain = 'bitcoin') => {
  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

  let txSent1Day = 0, txReceived1Day = 0;
  let txSent7Days = 0, txReceived7Days = 0;
  let txSent30Days = 0, txReceived30Days = 0;

  transactions.forEach(tx => {
    // Handle both Blockchain.com (tx.time) and Blockchair (tx.time as ISO string) formats
    const txTime = typeof tx.time === 'number'
      ? tx.time * 1000 // Blockchain.com: Unix timestamp in seconds
      : new Date(tx.time).getTime(); // Blockchair: ISO string

    // Calculate balance change for this address
    let received = 0;
    let sent = 0;

    if (chain === 'bitcoincash') {
      // Blockchair format: use balance_change
      const change = tx.balance_change || 0;
      if (change > 0) {
        received = change;
      } else if (change < 0) {
        sent = Math.abs(change);
      }
    } else {
      // Blockchain.com format
      // Check outputs (received)
      tx.out?.forEach(output => {
        if (output.addr === address) {
          received += output.value;
        }
      });

      // Check inputs (sent)
      tx.inputs?.forEach(input => {
        if (input.prev_out?.addr === address) {
          sent += input.prev_out.value;
        }
      });
    }

    // 1 day totals
    if (txTime >= oneDayAgo) {
      txReceived1Day += received;
      txSent1Day += sent;
    }

    // 7 days totals
    if (txTime >= sevenDaysAgo) {
      txReceived7Days += received;
      txSent7Days += sent;
    }

    // 30 days totals
    if (txTime >= thirtyDaysAgo) {
      txReceived30Days += received;
      txSent30Days += sent;
    }
  });

  return {
    txSent1Day: parseFloat(formatBitcoinBalance(txSent1Day)),
    txReceived1Day: parseFloat(formatBitcoinBalance(txReceived1Day)),
    txSent7Days: parseFloat(formatBitcoinBalance(txSent7Days)),
    txReceived7Days: parseFloat(formatBitcoinBalance(txReceived7Days)),
    txSent30Days: parseFloat(formatBitcoinBalance(txSent30Days)),
    txReceived30Days: parseFloat(formatBitcoinBalance(txReceived30Days)),
    totalSent: parseFloat(formatBitcoinBalance(totalSent)),
    totalReceived: parseFloat(formatBitcoinBalance(totalReceived)),
    totalTransactions: transactions.length
  };
};
